import React, { useEffect, useState, useMemo } from 'react'

import { ArrowLeft } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'

import { PeriodCycle } from '@/core/api/dtos/request/account/updateSubscription'
import { Subscription } from '@/core/api/entities/account/subscription'
import { PRODUCT_PLAYER_ID } from '@/core/api/entities/misc/product'
import { useRefetchAccountData } from '@/core/hooks/account/useRefetchAccountData'
import {
	useSubscribe,
	useGetSubscriptionPlans,
} from '@/core/hooks/account/useSubscription'
import { usePaymentTokens } from '@/core/hooks/payment/usePaymentTokens'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useMetaPixel } from '@/core/hooks/useMetaPixel'
import { useTranslation } from '@/core/i18n/useTranslation'
import { BillingInformationFormData } from '@/features/account/billing'
import PaymentMethods from '@/features/account/billing/components/PaymentMethods'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import { PaymentMethodError } from './components/PaymentMethodError'
import { PaymentMethodSuccess } from './components/PaymentMethodSuccess'
import { PaymentMethodVerifying } from './components/PaymentMethodVerifying'
import { SubscriptionStepper } from './components/SubscriptionStepper'
import { SubscriptionSuccessModal } from './components/SubscriptionSuccessModal'
import { SubscriptionSummary } from './components/SubscriptionSummary'
import { useMobileSubscribeStepScrollTop } from './hooks/useMobileSubscribeStepScrollTop'
import { BillingCycle, PlanConfig, PlanType } from './types'

// Default plan config for the summary (will be passed via navigation state)
const DEFAULT_PLAN: PlanConfig = {
	id: 'essential',
	name: 'Essential',
	description: '',
	priceMonthly: 15,
	priceAnnual: 12,
	currency: 'USD',
	hasZoneSelector: true,
}

interface LocationState {
	// Can be either the full PlanConfig (current flow) or a plan id/name string (stepper draft)
	plan?: PlanConfig | string
	billingCycle?: BillingCycle | string
	zonesCount?: number
	termsAccepted?: boolean
	// Allow carrying arbitrary draft data between steps
	[key: string]: unknown
}

const PaymentPage: React.FC = () => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const location = useLocation()
	const account = useCurrentAccount()
	const refetchAccountData = useRefetchAccountData()
	const { showNotification } = useNotificationContext()
	const { trackEvent } = useMetaPixel()
	const [showSuccessModal, setShowSuccessModal] = useState(false)

	useMobileSubscribeStepScrollTop()

	// Hard-coded guard to redirect if already subscribed (but not when showing success modal, since it's a success modal)
	// By using the useSubscribeGuard hook, we would redirect to the home page when the success modal is shown, which is not what we want.
	useEffect(() => {
		if (!account || showSuccessModal) return

		// Suspended accounts must stay on /subscribe so the blocked-account
		// modal is reachable; kicking them away causes a /subscribe <-> /zones loop.
		if (account.suspended) return

		// Check for active subscriptions that are not trials
		const activeSubscriptions = account.getActiveSubscriptions()
		const hasActivePaidSubscription = activeSubscriptions.some(
			(sub) => sub.status !== 'in_trial'
		)

		if (hasActivePaidSubscription) {
			navigate('/', { replace: true })
		}
	}, [account, navigate, showSuccessModal])

	// Track which steps have been completed successfully to prevent re-execution
	const [paymentMethodSaved, setPaymentMethodSaved] = useState(false)
	const [isRetrying, setIsRetrying] = useState(false)
	const [isSubmittingPaymentMethod, setIsSubmittingPaymentMethod] =
		useState(false)
	const [isVerifyingPaymentMethod, setIsVerifyingPaymentMethod] =
		useState(false)
	const [paymentMethodError, setPaymentMethodError] = useState(false)
	const [hasRetriedSubscribe, setHasRetriedSubscribe] = useState(false)

	// Fetch existing payment tokens to check if user already has a payment method
	const paymentTokensConfig = useAccountConfig()
	const {
		data: paymentTokens = [],
		isLoading: isLoadingPaymentTokens,
		isFetching: isFetchingPaymentTokens,
		refetch: refetchPaymentTokens,
	} = usePaymentTokens({
		config: paymentTokensConfig,
	})

	// Check if user already has an active payment token
	const hasExistingPaymentToken = useMemo(() => {
		return paymentTokens.some((token) => token.active)
	}, [paymentTokens])

	// Get state from navigation
	const state = location.state as LocationState | null

	// If no state (page reload), redirect to step 1
	useEffect(() => {
		if (!state || !state.plan) {
			navigate('/subscribe', { replace: true })
		}
	}, [state, navigate])
	const rawPlan = state?.plan
	const plan: PlanConfig =
		rawPlan && typeof rawPlan === 'object' && 'id' in rawPlan
			? (rawPlan as PlanConfig)
			: typeof rawPlan === 'string'
				? { ...DEFAULT_PLAN, id: rawPlan as PlanType, name: rawPlan }
				: DEFAULT_PLAN
	const billingCycle: BillingCycle =
		(state?.billingCycle as BillingCycle) || 'annual'

	// Get current subscription's zone quantity as fallback
	const currentSubscriptionZones = useMemo(() => {
		if (!account?.subscriptions) return 1

		const activeSubscription = account.subscriptions.find(
			(sub) => sub.status === 'active' || sub.status === 'in_trial'
		)

		if (!activeSubscription) return 1

		const playerQuantity = activeSubscription.getPlayerQuantity()
		return playerQuantity || 1
	}, [account?.subscriptions])

	const zonesCount = state?.zonesCount || currentSubscriptionZones
	const billingDraft = state?.billingDraft as
		| BillingInformationFormData
		| undefined

	const [termsAccepted, setTermsAccepted] = useState(
		state?.termsAccepted || false
	)

	// Minimal draft used by the stepper when navigating between steps
	const stepperDraft = {
		plan: plan.id,
		billingCycle,
		zonesCount,
		billingDraft,
	}

	// Full navigation state keeps any extra fields from previous steps
	const navigationState = {
		...(state ?? {}),
		plan,
		billingCycle,
		zonesCount,
		billingDraft,
		termsAccepted,
	}

	// Fetch subscription plans from API
	const { data: subscriptionPlans = [], isLoading: isLoadingPlans } =
		useGetSubscriptionPlans()

	// Get current subscription
	const currentSubscription = React.useMemo(() => {
		const subscriptions = account?.subscriptions || []
		// Find active or trial subscription
		return subscriptions.find(
			(sub) => sub.status === 'active' || sub.status === 'in_trial'
		)
	}, [account?.subscriptions])

	// Setup subscribe mutation
	const { mutate: subscribe } = useSubscribe({
		options: {
			onSuccess: (subscription: Subscription | void) => {
				// Track purchase event in Meta Pixel with real values from Stripe
				if (subscription) {
					trackEvent('Purchase', {
						currency: subscription.currency,
						value: subscription.total,
					})
				}

				void refetchAccountData()
				setShowSuccessModal(true)
				setIsRetrying(false)
				setIsVerifyingPaymentMethod(false)
			},
			onError: (error: any) => {
				console.error('❌ Subscription creation failed:', error)
				console.error('Error response:', error?.response)
				console.error('Error data:', error?.response?.data)
				console.error('Error message:', error?.message)

				// Start verification process
				setIsVerifyingPaymentMethod(true)
				setPaymentMethodError(false)

				// Wait 5 seconds then check payment methods
				setTimeout(async () => {
					try {
						// Refetch payment tokens to check if payment method was actually saved
						await refetchPaymentTokens()

						const hasActiveToken = Array.isArray(paymentTokens)
							? paymentTokens.some((token: any) => token.active)
							: false

						if (!hasActiveToken) {
							// No payment method found - reset state and show error
							setIsVerifyingPaymentMethod(false)
							setPaymentMethodSaved(false)
							setPaymentMethodError(true)
							setIsRetrying(false)
							showNotification({
								type: 'error',
								message: 'Payment method not registered',
								description:
									'Your payment method was not successfully registered. Please try adding your card again.',
							})
						} else if (!hasRetriedSubscribe) {
							// Payment method exists and we haven't retried yet - retry subscribe once
							setHasRetriedSubscribe(true)
							setIsRetrying(true)

							// Wait 2 seconds before retrying subscribe
							setTimeout(() => {
								proceedWithSubscription()
							}, 2000)
						} else {
							// Already retried and failed - show error
							setIsVerifyingPaymentMethod(false)
							setPaymentMethodSaved(false)
							setPaymentMethodError(true)
							setIsRetrying(false)
							showNotification({
								type: 'error',
								message: 'Failed to create subscription',
								description:
									error?.response?.data?.message ||
									error?.message ||
									'Please try adding your card again or contact support.',
							})
						}
					} catch (verifyError) {
						console.error('Error verifying payment method:', verifyError)
						setIsVerifyingPaymentMethod(false)
						setIsRetrying(false)
						showNotification({
							type: 'error',
							message: 'Failed to create subscription',
							description:
								error?.response?.data?.message ||
								error?.message ||
								'Please try again or contact support.',
						})
					}
				}, 5000)
			},
		},
	})

	const handleBack = () => {
		navigate('/subscribe/billing', {
			state: navigationState,
		})
	}

	// Helper function to proceed with subscription
	const proceedWithSubscription = React.useCallback(() => {
		// Map billing cycle to backend format
		const periodCycle: PeriodCycle =
			billingCycle === 'annual' ? PeriodCycle.YEARLY : PeriodCycle.MONTHLY

		// Get backend plan ID from API data by matching plan name
		const backendPlan = subscriptionPlans.find(
			(p) => p.name.toLowerCase() === plan.name.toLowerCase()
		)

		if (!backendPlan) {
			showNotification({
				type: 'error',
				message: 'Plan not found',
				description: `Could not find plan "${plan.name}" in available plans.`,
			})
			return
		}

		const subscriptionPayload = {
			plan_id: backendPlan.id,
			period_cycle: periodCycle,
			items: [
				{
					product_id: PRODUCT_PLAYER_ID, // Use product ID, not subscription item ID
					quantity: zonesCount,
				},
			],
		}

		subscribe(subscriptionPayload)
	}, [
		billingCycle,
		subscriptionPlans,
		plan.name,
		showNotification,
		zonesCount,
		subscribe,
	])

	const handlePaymentSuccess = async () => {
		setIsSubmittingPaymentMethod(false)

		// Mark payment method as saved after Stripe confirmation
		setPaymentMethodSaved(true)
		setPaymentMethodError(false)
		setHasRetriedSubscribe(false) // Reset retry flag for new payment method

		// Check if plans are still loading
		if (isLoadingPlans) {
			showNotification({
				type: 'info',
				message: 'Loading plans...',
				description: 'Please wait while we load subscription plans.',
			})
			return
		}

		// Check if plans loaded but empty
		if (subscriptionPlans.length === 0) {
			showNotification({
				type: 'error',
				message: 'Plans not available',
				description:
					'Could not load subscription plans. Please refresh the page.',
			})
			return
		}

		if (!currentSubscription) {
			showNotification({
				type: 'error',
				message: 'No active subscription found',
				description: 'Please contact support.',
			})
			return
		}

		// Wait 2 seconds after Stripe confirmation before calling subscribe
		setIsVerifyingPaymentMethod(true)
		await new Promise((resolve) => setTimeout(resolve, 2000))

		// Proceed to subscription
		proceedWithSubscription()
	}

	// Handle retry when subscription fails but payment and billing are already saved
	const handleRetrySubscription = () => {
		setIsRetrying(true)
		handlePaymentSuccess()
	}

	// Form ID for external submission from Summary button
	const PAYMENT_FORM_ID = 'payment-method-form'

	return (
		<AuthenticatedLayout hideSidebar topAppBarContent={<></>}>
			<div className="min-h-screen bg-background">
				{/* Header */}
				<div className="border-b border-border">
					<div className="max-w-7xl mx-auto px-6 py-4">
						<div className="flex items-center gap-4">
							<button
								onClick={handleBack}
								className="text-muted-foreground hover:text-foreground transition-colors"
							>
								<ArrowLeft className="h-5 w-5" />
							</button>
							<h1 className="text-xl font-medium text-muted-foreground">
								{t('subscribe.title')}
								<span className="text-foreground"> | </span>
								<span className="text-foreground">
									{t('subscribe.steps.payment')}
								</span>
							</h1>
						</div>
					</div>
				</div>

				{/* Stepper */}
				<div className="border-b border-border py-6 hidden lg:block">
					<SubscriptionStepper currentStep="payment" draft={stepperDraft} />
				</div>

				{/* Main Content */}
				<div className="max-w-7xl mx-auto px-6 py-8">
					<div className="flex gap-8 flex-col lg:flex-row">
						{/* Left Column - Payment Form */}
						<div className="flex-1">
							{/* Show loading skeleton while fetching payment tokens */}
							{isLoadingPaymentTokens || isFetchingPaymentTokens ? (
								<div className="bg-card border border-border rounded-xl p-6">
									<div className="space-y-4 animate-pulse">
										<div className="h-6 bg-muted rounded w-1/3" />
										<div className="h-4 bg-muted rounded w-2/3" />
										<div className="h-32 bg-muted rounded" />
									</div>
								</div>
							) : /* Show verification loader */ isVerifyingPaymentMethod ? (
								<PaymentMethodVerifying />
							) : /* Show error state if payment method failed */ paymentMethodError ? (
								<>
									<PaymentMethodError />
									<PaymentMethods
										onBack={handleBack}
										hideHeader
										forceShowAutomaticPayment
										defaultPaymentMethodType="card"
										onSuccess={handlePaymentSuccess}
										onSubmittingChange={setIsSubmittingPaymentMethod}
										showAddFormByDefault
										formId={PAYMENT_FORM_ID}
										hideInternalButtons
										showManualPayment={false}
									/>
								</>
							) : /* Show payment form only if no existing token and not saved in session */
							!paymentMethodSaved && !hasExistingPaymentToken ? (
								<PaymentMethods
									onBack={handleBack}
									hideHeader
									forceShowAutomaticPayment
									defaultPaymentMethodType="card"
									onSuccess={handlePaymentSuccess}
									onSubmittingChange={setIsSubmittingPaymentMethod}
									showAddFormByDefault
									formId={PAYMENT_FORM_ID}
									hideInternalButtons
									showManualPayment={false}
								/>
							) : (
								<PaymentMethodSuccess />
							)}
						</div>

						{/* Right Column - Summary */}
						<div className="w-full lg:w-80 flex-shrink-0">
							<div className="sticky top-8">
								<SubscriptionSummary
									plan={plan}
									billingCycle={billingCycle}
									zonesCount={zonesCount}
									onContinue={
										paymentMethodSaved || hasExistingPaymentToken
											? handleRetrySubscription
											: () => {}
									}
									buttonText={
										paymentMethodSaved || hasExistingPaymentToken
											? t('subscribe.complete_subscription', {
													defaultValue: 'Complete Subscription',
												})
											: 'Confirm Subscription'
									}
									formId={
										paymentMethodSaved || hasExistingPaymentToken
											? undefined
											: PAYMENT_FORM_ID
									}
									isLoading={
										isRetrying ||
										isSubmittingPaymentMethod ||
										isLoadingPaymentTokens ||
										isFetchingPaymentTokens ||
										isVerifyingPaymentMethod
									}
									isTermsAccepted={termsAccepted}
									onTermsAcceptedChange={setTermsAccepted}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<SubscriptionSuccessModal open={showSuccessModal} />
		</AuthenticatedLayout>
	)
}

export default PaymentPage
